import { Input, Row } from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Text from 'components/Text';
import { STAFF_GROUP_COLORS } from 'constants/calendar';
import { IStaffGroupUI } from 'types/staff-group';

const MAX_NAME_LENGTH = 30;

type Props = {
  /** Editing an existing group when set; creating a new one when undefined. */
  group?: IStaffGroupUI;
  onSubmit: (values: { name: string; colorHex: string }) => void;
  onCancel: () => void;
};

/** Name + color swatch form, shared by the create and edit flows. */
const GroupEditor = ({ group, onSubmit, onCancel }: Props) => {
  const [name, setName] = useState(group?.name || '');
  const [colorHex, setColorHex] = useState(group?.colorHex || STAFF_GROUP_COLORS[0]);

  useEffect(() => {
    setName(group?.name || '');
    setColorHex(group?.colorHex || STAFF_GROUP_COLORS[0]);
  }, [group]);

  const trimmedName = name.trim();

  return (
    <EditorBox>
      <Text variant="H8" color="text_3">{group ? 'Edit group' : 'New group'}</Text>
      <Input
        value={name}
        maxLength={MAX_NAME_LENGTH}
        placeholder="Group name (e.g. Floor 1)"
        onChange={e => setName(e.target.value)}
      />
      <SwatchRow>
        {STAFF_GROUP_COLORS.map(color => (
          <Swatch
            key={color}
            type="button"
            $color={color}
            $active={color === colorHex}
            aria-label={color}
            onClick={() => setColorHex(color)}
          />
        ))}
      </SwatchRow>
      <Row style={{ gap: 8 }}>
        <button className="btn btn-cancel" type="button" onClick={onCancel}>
          <Text className="label">CANCEL</Text>
        </button>
        <button
          className="btn btn-save"
          type="button"
          disabled={!trimmedName}
          onClick={() => onSubmit({ name: trimmedName, colorHex })}
        >
          <Text className="label">SAVE</Text>
        </button>
      </Row>
    </EditorBox>
  );
};

export default GroupEditor;

const EditorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid #E5E6EB;
  border-radius: 6px;

  input {
    height: 44px;
    font-size: 16px;
  }

  .btn {
    height: 44px;
    flex: 1;
    border-radius: 2px;
    .label {
      font-size: 16px;
      font-weight: 600;
    }
  }
  .btn-cancel {
    border: 1px solid #7B7B7B;
    background: #fff;
    .label { color: #7B7B7B; }
  }
  .btn-save {
    background: #232F3E;
    .label { color: #fff; }
    &:disabled { opacity: 0.5; }
  }
`;

const SwatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Swatch = styled.button<{ $color: string; $active: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${p => p.$color};
  border: ${p => p.$active ? '3px solid #1D2129' : '1px solid #E5E6EB'};
`;
